import { useEffect, useState } from 'react';
import { FormInput } from '@/components/FormInput';
import { useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { ResetPasswordType, useUpdatePasswordMutation } from '@/models/user';

type MessageType = {
  error: boolean;
  body: string;
};

export const ResetPassword = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<MessageType | null>(null);
  const [updatePassword, { isLoading, isError, error, isSuccess }] =
    useUpdatePasswordMutation();
  const { t } = useTranslation();
  const schema = yup.object({
    oldPassword: yup.string().required(t('validation.empty')),
    password: yup
      .string()
      .min(5, t('auth.register.validation.password_min_length'))
      .required(t('validation.empty')),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('password'), undefined],
        t('auth.register.validation.passwords_not_match')
      ),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResetPasswordType>({
    mode: 'onSubmit',
    // @ts-expect-error - TODO: fix this
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordType) => {
    await updatePassword(data);
  };

  useEffect(() => {
    if (isSuccess) {
      setOpen(true);
      setMessage({ error: false, body: t('label.reset_success') });
      reset();
    }
    if (isError) {
      const errMsg = error && 'status' in error && (error.data as any).message;
      setOpen(true);
      setMessage({ error: true, body: errMsg });
    }
  }, [isSuccess, isError, error, t, reset]);

  const handleClose = () => setOpen(false);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormInput
        name="oldPassword"
        label={t('label.old_password')}
        control={control}
        error={!!errors.oldPassword || (message?.error && message.body)}
        variant="outlined"
        fullWidth
        secured
      />

      <FormInput
        name="password"
        label={t('label.password')}
        control={control}
        error={!!errors.password}
        variant="outlined"
        fullWidth
        secured
      />

      <FormInput
        name="confirmPassword"
        label={t('auth.register.password_repeat')}
        control={control}
        error={!!errors.confirmPassword}
        variant="outlined"
        fullWidth
        secured
      />

      <div className="w-full flex flex-col">
        {open && !message?.error && (
          <div
            className={`${'bg-green-100 text-green-500 border-[1px] border-green-500'}  py-5 px-4 rounded-[10px] flex relative`}
          >
            {message?.body}
            <IconButton
              onClick={handleClose}
              style={{ top: 3, right: 3, position: 'absolute' }}
            >
              <Close />
            </IconButton>
          </div>
        )}

        <div className="flex justify-end mt-3">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            color="secondary"
          >
            {t('label.reset')}
          </LoadingButton>
        </div>
      </div>
    </form>
  );
};
